<script setup>
import { Head, Link, useForm } from '@inertiajs/vue3';
import AuthenticationCard from '@/Components/AuthenticationCard.vue';
import AuthenticationCardLogo from '@/Components/AuthenticationCardLogo.vue';
import Checkbox from '@/Components/BaseElements/Checkbox.vue';
import InputError from '@/Components/BaseElements/InputError.vue';
import InputLabel from '@/Components/BaseElements/InputLabel.vue';
import PrimaryButton from '@/Components/BaseElements/PrimaryButton.vue';
import TextInput from '@/Components/BaseElements/TextInput.vue';
import SecondaryButton from "@/Components/BaseElements/SecondaryButton.vue";

const props = defineProps({
    canResetPassword: Boolean,
    status: String,
    env: String,
});

const form = useForm({
    email: '',
    password: '',
    remember: false,
});

const submit = () => {
    form.transform(data => ({
        ...data,
        remember: form.remember ? 'on' : '',
    })).post(route('login'), {
        onFinish: () => form.reset('password'),
    });
};

const debugLogin = (email, password) => {
    form.email = email;
    form.password = password;
    submit();
};

const openReservationDebugForm = () => {
    window.location.href = '/reservation-debug-form'
}
</script>

<template>
    <Head title="Log in" />

    <AuthenticationCard>
        <template #logo>
            <AuthenticationCardLogo />
        </template>

        <!-- Debug Section: Only visible in local or staging -->
        <div v-if="env === 'local' || env === 'staging'" class="debug-container">
            <h4>Debug Options</h4>
            <PrimaryButton @click="debugLogin('gajdosiktomas@gmail.com', 'nbusr123')" class="ms-4" :class="{ 'opacity-25': form.processing }" :disabled="form.processing">
                Login as Tomas Gajdosik Admin
            </PrimaryButton>
            <PrimaryButton @click="debugLogin('michalkmet.pd@gmail.com', 'nbusr123')" class="mt-4 ms-4" :class="{ 'opacity-25': form.processing }" :disabled="form.processing">
                Login as Michal Kmet Admin
            </PrimaryButton>

            <PrimaryButton @click="debugLogin('admin@pp5.sk', 'nbusr123')" class="mt-4 ms-4" :class="{ 'opacity-25': form.processing }" :disabled="form.processing">
                Login as Owner PD
            </PrimaryButton>

            <PrimaryButton @click="debugLogin('servis@pp5.sk', 'nbusr123')" class="mt-4 ms-4" :class="{ 'opacity-25': form.processing }" :disabled="form.processing">
                Login as Servis PP5
            </PrimaryButton>

            <SecondaryButton @click="openReservationDebugForm()" class="mt-4 ms-4">
                Open Reservation Debug Form
            </SecondaryButton>
        </div>

        <div v-if="status" class="mb-4 font-medium text-sm text-green-600 dark:text-green-400">
            {{ status }}
        </div>

        <form @submit.prevent="submit">
            <div>
                <InputLabel for="email" value="Email" />
                <TextInput
                    id="email"
                    v-model="form.email"
                    type="email"
                    class="mt-1 block w-full"
                    required
                    autofocus
                    autocomplete="username"
                />
                <InputError class="mt-2" :message="form.errors.email" />
            </div>

            <div class="mt-4">
                <InputLabel for="password" value="Password" />
                <TextInput
                    id="password"
                    v-model="form.password"
                    type="password"
                    class="mt-1 block w-full"
                    required
                    autocomplete="current-password"
                />
                <InputError class="mt-2" :message="form.errors.password" />
            </div>

            <div class="block mt-4">
                <label class="flex items-center">
                    <Checkbox v-model:checked="form.remember" name="remember" />
                    <span class="ms-2 text-sm text-gray-600 dark:text-gray-400">Remember me</span>
                </label>
            </div>

            <div class="flex items-center justify-end mt-4">
                <Link
                    v-if="canResetPassword"
                    :href="route('password.request')"
                    class="underline text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 rounded-md focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 dark:focus:ring-offset-gray-800"
                >
                    Forgot your password?
                </Link>

                <PrimaryButton class="ms-4" :class="{ 'opacity-25': form.processing }" :disabled="form.processing">
                    Log in
                </PrimaryButton>
            </div>
        </form>
    </AuthenticationCard>
</template>

<style scoped>
/* Debug Section Container */
.debug-container {
    background-color: #cd5c5c;
    padding: 1rem;
    margin-bottom: 1rem;
}

/* Debug Header */
h4 {
    color: #ffffff;
    margin-bottom: 1rem;
}
</style>
